import { Phone, Mail, MapPin, Clock } from "lucide-react";
import { Reveal } from "../components/Reveal";
import { Seo } from "../components/Seo";
import { AssessmentForm } from "../components/AssessmentForm";
import { CONTACT } from "../data/site";

const INFO = [
  { icon: MapPin, label: "Service area", value: CONTACT.area },
  { icon: Phone, label: "Phone", value: CONTACT.phone, href: CONTACT.phoneHref, testid: "contact-phone" },
  { icon: Mail, label: "Email", value: CONTACT.email, href: CONTACT.emailHref, testid: "contact-email" },
  { icon: Clock, label: "Support", value: "Proactive monitoring & responsive help" },
];

export default function Contact() {
  return (
    <div data-testid="contact-page">
      <Seo
        title="Contact Zentryx Systems — Atlanta Managed IT & Cybersecurity | Free IT Assessment"
        description="Talk to Zentryx Systems, an Atlanta managed IT and cybersecurity provider. Call 404-860-2291 or request a free, no-obligation IT assessment for your business."
        path="/contact"
      />
      <section className="pt-32 sm:pt-40 pb-16 px-5 sm:px-8 bg-mist">
        <div className="max-w-5xl mx-auto text-center">
          <Reveal>
            <p className="text-sm uppercase tracking-widest font-semibold text-teal mb-4">Free IT Assessment</p>
            <h1 className="font-heading text-4xl sm:text-5xl lg:text-6xl font-extrabold tracking-tight text-charcoal leading-tight">
              Let's talk about your firm's technology.
            </h1>
            <p className="mt-6 text-lg text-slate-600 max-w-2xl mx-auto leading-relaxed">
              Book a free, no-obligation IT assessment. We'll review your infrastructure, security, and roadmap — and give you clear, prioritized recommendations. Prefer to talk now? Call{" "}
              <a href={CONTACT.phoneHref} className="text-navy font-semibold hover:underline">{CONTACT.phone}</a>.
            </p>
          </Reveal>
        </div>
      </section>

      <section className="py-20 sm:py-28 px-5 sm:px-8">
        <div className="max-w-7xl mx-auto grid lg:grid-cols-5 gap-12 items-start">
          <Reveal className="lg:col-span-2">
            <h2 className="font-heading text-2xl font-bold text-charcoal mb-6">Get in touch</h2>
            <div className="space-y-5">
              {INFO.map((it) => (
                <div key={it.label} className="flex items-start gap-4">
                  <div className="h-11 w-11 rounded-2xl bg-navy/5 flex items-center justify-center flex-shrink-0">
                    <it.icon className="h-5 w-5 text-navy" />
                  </div>
                  <div>
                    <p className="text-xs uppercase tracking-widest font-semibold text-slate-400">{it.label}</p>
                    {it.href ? (
                      <a href={it.href} data-testid={it.testid} className="text-charcoal font-medium mt-0.5 block hover:text-navy transition-colors break-all">{it.value}</a>
                    ) : (
                      <p className="text-charcoal font-medium mt-0.5">{it.value}</p>
                    )}
                  </div>
                </div>
              ))}
            </div>
            <div className="mt-10 rounded-3xl bg-navy text-white p-8">
              <p className="font-heading text-lg font-bold">What happens next?</p>
              <ol className="mt-4 space-y-3 text-sm text-slate-300">
                <li>1. We review your request and reach out within one business day.</li>
                <li>2. We conduct a no-obligation assessment of your environment.</li>
                <li>3. You receive a clear, prioritized roadmap — yours to keep.</li>
              </ol>
            </div>
          </Reveal>

          <Reveal delay={0.12} className="lg:col-span-3">
            <div className="rounded-3xl border border-slate-200 bg-white p-6 sm:p-10 shadow-sm">
              <AssessmentForm />
            </div>
          </Reveal>
        </div>
      </section>
    </div>
  );
}
